import {
  Box,
  FlexBox,
  Icon,
  Image,
  Text,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';

import { ArticleQuery } from 'components/conversation/components/integrations/elevio/src/apis/graphQl/elevio/grappa/types/grappa.types';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { elevioActions } from '../../elevio.actions';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { useArticle } from '../../hooks';
import { ElevioBaseVariables, Article as IArticle } from '../../types';
import { ElevioError, getUrl } from '../../utils';
import { ArticleFeedback } from '../ArticleFeedback';
import { CopyArticleLink } from '../CopyArticleLink';
import { OpenArticleInTab } from '../OpenArticleInTab';
import { SmartConversationReplyButton } from '../SmartConversationReplyButton';

import styles from './Article.module.scss';
import { ArticlePlaceholder } from './ArticlePlaceholder';

import { ArticleBody } from './index';

type ArticleHeaderProps = {
  articleSlug: string;
  article: IArticle;
};
function ArticleHeader({
  articleSlug,
  article,
}: ArticleHeaderProps): JSX.Element {
  const dispatch = useAppDispatch();
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const onBack = () => {
    if (!conversationId) return;
    dispatch(elevioActions.suggestions.articleNavBack({ conversationId }));
  };
  const { hasCopilot } = useCopilotTrial();

  const tabStatus = useAppSelector(elevioSelectors.smartReply.tabStatus);
  const conversationReplyModeActive = tabStatus === 'reply';

  return (
    <FlexBox className={styles.articleHeader} gap="300" alignItems="center">
      <Icon className={styles.icon} name="arrow-left" onClick={onBack} />
      <Box className={styles.grow}>
        <Text.Paragraph className={styles.results} variant="3">
          Results
        </Text.Paragraph>
      </Box>
      {conversationReplyModeActive && hasCopilot && (
        <SmartConversationReplyButton articleBody={article.bodyText} />
      )}
      <CopyArticleLink articleSlug={articleSlug} />
      <OpenArticleInTab articleSlug={articleSlug} />
    </FlexBox>
  );
}

function ArticleFeaturedImage({
  featuredImageUrl,
  articleTitle,
}: {
  featuredImageUrl: string | undefined;
  articleTitle: string;
}) {
  if (!featuredImageUrl) return null;

  return (
    <Image
      alt={`Image for article: "${articleTitle}"`}
      className={styles.featuredImage}
      src={featuredImageUrl}
    />
  );
}

function ArticleTitle({ articleTitle }: { articleTitle: string }) {
  return (
    <Text.Heading className={styles.title} variant="heading-1" color="primary">
      {articleTitle}
    </Text.Heading>
  );
}

const iconColor = typography.color['tertiary'];

function ArticleMeta({
  article,
}: {
  article: ArticleQuery['article'];
}): JSX.Element | null {
  if (!article) return null;
  const {
    lastPublishedAt,
    category,
    tags,
    accessControl: { isInternal },
  } = article;
  return (
    <FlexBox className={styles.articleMeta} gap="8px" flexDirection="column">
      <FlexBox gap="4px" alignItems="center">
        <Icon
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          size={12}
          /* eslint-enable @typescript-eslint/ban-ts-comment */
          name="eye"
          color={iconColor}
        />
        <Text.Paragraph variant="4" color="tertiary">
          {isInternal ? 'Private' : 'Public'}
        </Text.Paragraph>
      </FlexBox>
      <FlexBox gap="4px" alignItems="center">
        <Icon
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          size={12}
          /* eslint-enable @typescript-eslint/ban-ts-comment */
          name="pen"
          color={iconColor}
        />
        <Box>
          <Text.Paragraph variant="4" color="tertiary">
            {`Last updated`}{' '}
            <Timestamp time={lastPublishedAt / 1000} relative={true} />
          </Text.Paragraph>
        </Box>
      </FlexBox>
      <FlexBox gap="4px" alignItems="center">
        <Icon
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          size={12}
          /* eslint-enable @typescript-eslint/ban-ts-comment */
          name="address-book"
          color={iconColor}
        />
        <Box>
          <Text.Paragraph variant="4" color="tertiary">
            {category.title}
          </Text.Paragraph>
        </Box>
      </FlexBox>
      {!!tags.length && (
        <FlexBox gap="4px" alignItems="center">
          <Icon
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            size={12}
            /* eslint-enable @typescript-eslint/ban-ts-comment */
            name="tag"
            color={iconColor}
          />
          <Box>
            <Text.Paragraph variant="4" color="tertiary">
              {tags.join(', ')}
            </Text.Paragraph>
          </Box>
        </FlexBox>
      )}
    </FlexBox>
  );
}

type ArticleProps = {
  id: string;
} & ElevioBaseVariables;

export function Article(props: ArticleProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { error, isFetching, articleData } = useArticle(props);
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const conversationStatus = useAppSelector(
    elevioSelectors.conversation.status,
  );
  const tagNames = useAppSelector(elevioSelectors.conversation.tagNames);

  const onClickInterlink = (articleId: string) => {
    if (!conversationId) return;
    dispatch(
      elevioActions.suggestions.viewArticle({
        articleId,
        ticketStatus: conversationStatus,
        ticketTags: tagNames,
        url: getUrl(),
        ticketId: conversationId.toString(),
        languageId: props.languageId,
      }),
    );
  };

  if (error) {
    throw ElevioError({
      feature: 'suggestions',
      context: 'Article component',
      message: error.message,
    });
  }

  if (isFetching) return <ArticlePlaceholder />;

  if (!articleData || !articleData.article) return null;
  const articleSlug = articleData.article.slug;
  const articleTitle = articleData.article.title;
  const featuredImageUrl = articleData.article.featuredImageUrl ?? '';

  return (
    <Box>
      <ArticleHeader articleSlug={articleSlug} article={articleData.article} />
      <ArticleFeaturedImage
        articleTitle={articleTitle}
        featuredImageUrl={featuredImageUrl}
      />
      <ArticleTitle articleTitle={articleTitle} />
      <ArticleMeta article={articleData.article} />
      <ArticleBody
        article={articleData.article}
        onClickInterlink={onClickInterlink}
      />
      <ArticleFeedback />
    </Box>
  );
}
